import { MouseEvent, useMemo } from "react";
import { Empty, Flex } from "antd";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useTranslate } from "@refinedev/core";
import { parseScript } from "pages/media/helpers/scriptAnchors";
import { parseScriptBySlides } from "pages/media/projects/components/slides/utils";
import { useTemplateEditorStore } from "../../store/useTemplateEditorStore";
import { useTemplatesStatusStore } from "../../store/useTemplatesStatusStore";
import { useChangeLayer } from "../../hooks/useChangeLayer";
import { useTemplateData } from "../../hooks/useTemplateData";
import { getFirstSentence } from "../../helpers";
import { resolveMainImageMediaType } from "../../helpers/itemsHelper";
import {
  getBackgroundDescriptor,
  getLayerDescriptors,
  LayerDescriptor,
  LayerStoreItem,
} from "../../helpers/layerItemDescriptor";
import { getTabForItemType } from "../../helpers/itemTypeToTab";
import {
  CustomText,
  ITEM_TYPES,
  ItemType,
  MediaItem,
  Shape,
  TabKeys,
} from "../../types";
import { SortableLayerRow, StaticLayerRow } from "./LayerRow";
import { restrictToVerticalAxis } from "./dndModifiers";
import { nanoid } from "nanoid";

// Item kinds that can be duplicated from the layers panel.
const DUPLICABLE_KINDS: ItemType["type"][] = [
  ITEM_TYPES.Image,
  ITEM_TYPES.Video,
  ITEM_TYPES.Shape,
  ITEM_TYPES.CustomText,
];
// Relative offset applied to a duplicated item so it doesn't overlap the source.
const DUPLICATE_OFFSET = 0.02;

const isSameItem = (current: ItemType | undefined, target: ItemType) => {
  if (!current) return false;
  if (target.id) return current.id === target.id;
  // Singletons have no id — match on type and the absence of an id.
  return current.type === target.type && !current.id;
};

export const LayersPanel = () => {
  const t = useTranslate();

  const {
    templatesData: { isFlashProject },
    storyboardSections,
    storyboardWithSlidesData: { slides },
  } = useTemplateData();

  const {
    templateState,
    view,
    currentItem,
    selectedItemIds,
    currentSlide,
    sectionId,
    chapterTopic,
    setCurrentItem,
    toggleSelection,
    deleteImage,
    deleteVideo,
    deleteCustomText,
    deleteShape,
    deleteMainImage,
    updateTemplateState,
    addImage,
    addVideo,
    addShape,
    addCustomText,
  } = useTemplateEditorStore((store) => ({
    templateState: store.templateState,
    view: store.currentChapter.type,
    currentItem: store.templateState.currentItem,
    selectedItemIds: store.selectedItemIds,
    currentSlide: store.currentSlide,
    sectionId: store.currentChapter.sectionId,
    chapterTopic: store.currentChapter.topic,
    setCurrentItem: store.setCurrentItem,
    toggleSelection: store.toggleSelection,
    deleteImage: store.deleteImage,
    deleteVideo: store.deleteVideo,
    deleteCustomText: store.deleteCustomText,
    deleteShape: store.deleteShape,
    deleteMainImage: store.deleteMainImage,
    updateTemplateState: store.updateTemplateState,
    addImage: store.addImage,
    addVideo: store.addVideo,
    addShape: store.addShape,
    addCustomText: store.addCustomText,
  }));

  const { activeTab, setActiveTab, addTemplateUpdate } =
    useTemplatesStatusStore((state) => ({
      activeTab: state.activeTab,
      setActiveTab: state.setActiveTab,
      addTemplateUpdate: state.addTemplateUpdate,
    }));

  const { reorderLayers, newHighestLayerIndex } = useChangeLayer();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  // The subtitles layer has no stored text; mirror the canvas, which shows the
  // scene's narration extracted from the storyboard section script (per-slide
  // for Flash, whole-section otherwise).
  const subtitleText = useMemo(() => {
    const resolvedSectionId =
      sectionId || slides.find((s) => s.uuid === currentSlide)?.section_id;
    const script = resolvedSectionId
      ? storyboardSections.find((s) => s.id === resolvedSectionId)?.script
          .content
      : undefined;
    if (!script) return undefined;
    const slideScript = currentSlide
      ? parseScriptBySlides(script).get(currentSlide)
      : undefined;
    return getFirstSentence(parseScript(slideScript ?? script).text);
  }, [sectionId, currentSlide, slides, storyboardSections]);

  // The default main media carries no mediaType on the store item; resolve it
  // from the current slide so a video main image shows the Video icon/name.
  const mainImageMediaType = useMemo(
    () =>
      resolveMainImageMediaType(
        templateState.mainImage,
        slides.find((s) => s.uuid === currentSlide)
      ),
    [templateState.mainImage, slides, currentSlide]
  );

  // Layers come from the current `templateState` (which is the chapter default
  // even before any template is saved), so a non-Flash project always has a
  // selected chapter to show. Flash projects key off the selected slide.
  const hasSelection = isFlashProject ? !!currentSlide : true;

  if (!hasSelection) {
    return (
      <Flex justify="center" style={{ padding: 16 }}>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(
            "components.templates.TemplatesBoard.storyboardLeftPanel.noSceneSelected"
          )}
        />
      </Flex>
    );
  }

  // Ascending (back-to-front) for reorder math; reversed for display.
  const descriptors = getLayerDescriptors(templateState, view, {
    chapterTopic,
    subtitleText,
    mainImageMediaType,
  });
  const displayable = descriptors.filter((d) => d.displayable);
  const rows = [...displayable].reverse();
  const backgroundRow = getBackgroundDescriptor(templateState.background);

  const handleSelect = (descriptor: LayerDescriptor, e?: MouseEvent) => {
    // Cmd/Ctrl+click toggles the row into the multi-selection, mirroring the
    // canvas (DraggableComponent). The selection key matches isLayerSelected.
    if (e && (e.metaKey || e.ctrlKey)) {
      const key = descriptor.selectItem.id ?? descriptor.selectItem.type;
      const next = selectedItemIds.includes(key)
        ? selectedItemIds.filter((k) => k !== key)
        : [...selectedItemIds, key];
      toggleSelection(key);
      // Restore single-item settings only when this becomes the sole selection.
      if (next.length === 1 && next[0] === key) {
        setCurrentItem(descriptor.selectItem);
      }
      return;
    }
    setCurrentItem(descriptor.selectItem);
    const tab = getTabForItemType(descriptor.kind);
    if (tab && activeTab !== TabKeys.motion) {
      setActiveTab(tab);
    }
  };

  // A layer row is highlighted when its item is part of the current selection
  // (works for both single and multi-selection).
  const isLayerSelected = (descriptor: LayerDescriptor) =>
    selectedItemIds.includes(
      descriptor.selectItem.id ?? descriptor.selectItem.type
    );

  const deleteMediaItem = (kind: ItemType["type"], id: string) => {
    if (templateState.mainImage?.id === id) deleteMainImage();
    else if (kind === ITEM_TYPES.Image) deleteImage(id);
    else deleteVideo(id);
  };

  // Slide document text can't be removed structurally, so hide it instead.
  const hideParameteredText = (id: string) => {
    const { parameteredTexts, figure } = templateState;
    if (parameteredTexts?.some((p) => p.id === id)) {
      updateTemplateState({
        parameteredTexts: parameteredTexts.map((p) =>
          p.id === id ? { ...p, visible: false } : p
        ),
      });
    } else if (figure?.number.id === id) {
      updateTemplateState({
        figure: { ...figure, number: { ...figure.number, visible: false } },
      });
    } else if (figure?.units?.id === id) {
      updateTemplateState({
        figure: { ...figure, units: { ...figure.units, visible: false } },
      });
    }
  };

  const handleDelete = (descriptor: LayerDescriptor) => {
    const id = descriptor.selectItem.id;

    switch (descriptor.kind) {
      case ITEM_TYPES.Image:
      case ITEM_TYPES.Video:
        if (id) deleteMediaItem(descriptor.kind, id);
        break;
      case ITEM_TYPES.CustomText:
        if (id) deleteCustomText(id);
        break;
      case ITEM_TYPES.Shape:
        if (id) deleteShape(id);
        break;
      case ITEM_TYPES.ParameteredText:
        if (id) hideParameteredText(id);
        break;
      case ITEM_TYPES.Table:
        updateTemplateState({ table: undefined });
        break;
      case ITEM_TYPES.Logo:
        updateTemplateState({
          logo: { ...templateState.logo, showLogo: false },
        });
        break;
      case ITEM_TYPES.Presenter:
        updateTemplateState({
          presenter: { ...templateState.presenter, presenter: undefined },
        });
        break;
      case ITEM_TYPES.Subtitles:
        updateTemplateState({
          textStyle: { ...templateState.textStyle, showSubtitles: false },
        });
        break;
      case ITEM_TYPES.Title:
        updateTemplateState({
          titleStyle: { ...templateState.titleStyle, showTitle: false },
        });
        break;
      default:
        return;
    }

    // Clear the canvas selection if the deleted item was selected.
    if (isSameItem(currentItem, descriptor.selectItem)) {
      setCurrentItem(undefined);
    }
  };

  const isPositionedItem = (
    item: LayerStoreItem
  ): item is MediaItem | Shape | CustomText =>
    "position" in item && "size" in item;

  const handleDuplicate = (descriptor: LayerDescriptor) => {
    const item = descriptor.item;
    if (!isPositionedItem(item)) return;
    const width = item.size.width;
    const height = item.size.height;
    let x = item.position.x + DUPLICATE_OFFSET;
    let y = item.position.y + DUPLICATE_OFFSET;
    if (x + width > 1) x = Math.max(0, 1 - width);
    if (y + height > 1) y = Math.max(0, 1 - height);

    const id = nanoid();
    const base = {
      ...item,
      id,
      position: { x, y },
      layerIndex: newHighestLayerIndex,
    };
    switch (descriptor.kind) {
      case ITEM_TYPES.Image:
        addImage(base as MediaItem);
        break;
      case ITEM_TYPES.Video:
        addVideo(base as MediaItem);
        break;
      case ITEM_TYPES.Shape:
        addShape(base as Shape);
        break;
      case ITEM_TYPES.CustomText:
        addCustomText(base as CustomText);
        break;
      default:
        return;
    }
    // Select the freshly-created duplicate.
    setCurrentItem({ type: descriptor.kind, id });
    addTemplateUpdate();
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = descriptors.findIndex((d) => d.id === active.id);
    const toIndex = descriptors.findIndex((d) => d.id === over.id);
    if (fromIndex === -1 || toIndex === -1) return;
    reorderLayers(
      descriptors.map((d) => d.item),
      fromIndex,
      toIndex
    );
  };

  return (
    <Flex vertical style={{ width: "100%", paddingBlockEnd: 64 }}>
      {/* A selected scene always has at least the pinned background row, so
          there is no "no items" empty state here (only the no-scene one above). */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={rows.map((d) => d.id)}
          strategy={verticalListSortingStrategy}
        >
          {rows.map((descriptor) => (
            <SortableLayerRow
              key={descriptor.id}
              descriptor={descriptor}
              selected={isLayerSelected(descriptor)}
              onSelect={(e) => handleSelect(descriptor, e)}
              onDelete={
                descriptor.deletable
                  ? () => handleDelete(descriptor)
                  : undefined
              }
              onDuplicate={
                DUPLICABLE_KINDS.includes(descriptor.kind)
                  ? () => handleDuplicate(descriptor)
                  : undefined
              }
            />
          ))}
        </SortableContext>
      </DndContext>
      <StaticLayerRow
        descriptor={backgroundRow}
        selected={isLayerSelected(backgroundRow)}
        onSelect={() => handleSelect(backgroundRow)}
      />
    </Flex>
  );
};
